import React, { useMemo, useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { MenuItem } from "@/model/entities";
import { getFirebaseImageUrl } from "@/service/firebaseService";

interface MenuItemViewerCardProps {
  item: MenuItem;
  onClick?: () => void;
  // allow parent to pass additional classes (for example to force height)
  className?: string;
}

const MenuItemViewerCard: React.FC<MenuItemViewerCardProps> = ({
  item,
  onClick,
  className,
}) => {
  const [defaultImageUrl, setDefaultImageUrl] = useState<string>("");

  useEffect(() => {
    getFirebaseImageUrl("static/default-menu-item.webp").then(
      setDefaultImageUrl
    );
  }, []);

  const formattedPrice = useMemo(() => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
    }).format(item.price);
  }, [item]);

  return (
    <Card
      className={cn(
        "overflow-hidden hover:shadow-lg transition-shadow py-0 rounded-lg",
        className
      )}
      onClick={onClick}
      data-testid="menu-item-card"
    >
      <div className="aspect-[4/3] relative overflow-hidden rounded-t-lg">
        {!defaultImageUrl ? (
          <Skeleton className="object-cover w-full h-full" />
        ) : (
          <img
            src={item.imageUrl ?? defaultImageUrl}
            alt={item.name}
            className="object-cover w-full h-full transform hover:scale-105 transition-transform duration-300 rounded-t-lg"
            data-testid="menu-item-image"
          />
        )}
      </div>

      <CardContent className="p-1 -mt-1 flex flex-col gap-0 justify-start text-start">
        <div>
          <h2
            className="text-sm font-semibold text-gray-800 truncate"
            data-testid="menu-item-name"
          >
            {item.name}
          </h2>
          <p
            className="text-xs text-gray-600 line-clamp-2 mt-0"
            data-testid="menu-item-description"
          >
            {item.description}
          </p>
        </div>

        <div className="mt-2">
          <span
            className="text-sm font-bold text-green-600"
            data-testid="menu-item-price"
          >
            {formattedPrice}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default MenuItemViewerCard;
